/**
 * Convert any browser-decodable image to a compressed JPEG before upload.
 * Contract: listing photos are stored as JPEG — WebP/HEIC would skip the stolen-photo dHash check.
 * Downscales so the long edge is ≤ 2560px. HEIC only decodes in Safari; elsewhere the user is told
 * to export as JPEG.
 */
import { ApiError } from '@/api/api';

const MAX_EDGE = 2560;
const QUALITY = 0.85;

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* fall through to <img> decode */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function toJpeg(file: File): Promise<File> {
  const isImage = file.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp|gif|avif)$/i.test(file.name);
  if (!isImage) throw new ApiError('Only photos can be uploaded here.', 'upload');
  let src: ImageBitmap | HTMLImageElement;
  try {
    src = await decode(file);
  } catch {
    throw new ApiError(
      /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type)
        ? 'This browser can’t read HEIC photos. Export it as JPEG (on iPhone: Settings → Camera → Formats → Most Compatible) and try again.'
        : 'This photo couldn’t be read. Try saving it as a JPEG.',
      'upload',
    );
  }
  const w = 'naturalWidth' in src ? src.naturalWidth : src.width;
  const h = 'naturalHeight' in src ? src.naturalHeight : src.height;
  if (!w || !h) throw new ApiError('This photo appears to be empty.', 'upload');
  if (Math.min(w, h) < 400) throw new ApiError(`“${file.name}” is too small (${w}×${h}). Use photos at least 400px on the short side.`, 'upload');
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const cw = Math.round(w * scale);
  const ch = Math.round(h * scale);
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ApiError('Your browser couldn’t process this photo.', 'upload');
  ctx.fillStyle = '#ffffff'; // flatten transparency (PNG) onto white
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(src as CanvasImageSource, 0, 0, cw, ch);
  if ('close' in src && typeof src.close === 'function') src.close();
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', QUALITY));
  if (!blob) throw new ApiError('Your browser couldn’t compress this photo.', 'upload');
  const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}
